import React, { createContext, useContext } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

const inputGroupStructureClasses = [
  'group/input-group relative flex w-full items-center',
  'rounded-md border border-input',
  'shadow-xs transition-[color,box-shadow] outline-none',
  'min-w-0 has-[>textarea]:h-auto',
];

const inputGroupAlignmentClasses = [
  'has-[>[data-align=inline-start]]:[&>input]:pl-2',
  'has-[>[data-align=inline-end]]:[&>input]:pr-2',
  'has-[>[data-align=block-start]]:h-auto has-[>[data-align=block-start]]:flex-col has-[>[data-align=block-start]]:[&>input]:pb-3',
  'has-[>[data-align=block-end]]:h-auto has-[>[data-align=block-end]]:flex-col has-[>[data-align=block-end]]:[&>input]:pt-3',
];

const inputGroupStateClasses = [
  'has-[[data-slot=input-group-control]:focus-visible]:border-input',
  'has-[[data-slot=input-group-control]:focus-visible]:ring-[3px]',
  'has-[[data-slot=input-group-control]:focus-visible]:ring-ring/50',
  'has-[[data-slot][aria-invalid=true]]:border-destructive',
  'has-[[data-slot][aria-invalid=true]]:ring-destructive/20',
];

const inputGroupVariants = cva(
  [...inputGroupStructureClasses, ...inputGroupAlignmentClasses, ...inputGroupStateClasses],
  {
    variants: {
      size: {
        md: 'h-9',
        lg: 'h-11',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
);

type InputGroupSize = NonNullable<VariantProps<typeof inputGroupVariants>['size']>;

const InputGroupSizeContext = createContext<InputGroupSize>('md');

type InputGroupProps = React.ComponentProps<'div'> & VariantProps<typeof inputGroupVariants>;

function InputGroup({ className, size, children, ...props }: InputGroupProps) {
  const computedSize: InputGroupSize = size ?? 'md';

  return (
    <InputGroupSizeContext.Provider value={computedSize}>
      <div
        data-slot="input-group"
        data-size={computedSize}
        className={cn(inputGroupVariants({ size: computedSize }), className)}
        {...props}
      >
        {children}
      </div>
    </InputGroupSizeContext.Provider>
  );
}

const inputGroupAddonVariants = cva(
  "flex h-auto cursor-text items-center justify-center gap-2 font-medium select-none group-data-[disabled=true]/input-group:opacity-50 [&>kbd]:rounded-[calc(var(--radius)-5px)] [&>svg:not([class*='size-'])]:size-4",
  {
    variants: {
      size: {
        md: 'py-1.5 text-sm',
        lg: 'py-2 text-base',
      },
      align: {
        'inline-start': 'order-first pl-3 has-[>button]:ml-[-0.45rem] has-[>kbd]:ml-[-0.35rem]',
        'inline-end': 'order-last pr-3 has-[>button]:mr-[-0.45rem] has-[>kbd]:mr-[-0.35rem]',
        'block-start':
          'order-first w-full justify-start px-3 pt-3 group-has-[>input]/input-group:pt-2.5 [.border-b]:pb-3',
        'block-end':
          'order-last w-full justify-start px-3 pb-3 group-has-[>input]/input-group:pb-2.5 [.border-t]:pt-3',
      },
    },
    defaultVariants: {
      size: 'md',
      align: 'inline-start',
    },
  },
);

function InputGroupAddon({
  className,
  align = 'inline-start',
  size,
  ...props
}: React.ComponentProps<'span'> & VariantProps<typeof inputGroupAddonVariants>) {
  const contextSize = useContext(InputGroupSizeContext);
  const computedSize = size ?? contextSize;

  return (
    <span
      data-slot="input-group-addon"
      data-align={align}
      data-size={computedSize}
      className={cn(inputGroupAddonVariants({ align, size: computedSize }), className)}
      {...props}
    />
  );
}

const inputGroupButtonVariants = cva('flex items-center gap-2 text-sm shadow-none', {
  variants: {
    size: {
      xs: "h-6 gap-1 rounded-[calc(var(--radius)-5px)] px-2 has-[>svg]:px-2 [&>svg:not([class*='size-'])]:size-3.5",
      sm: 'h-8 gap-1.5 rounded-md px-2.5 has-[>svg]:px-2.5',
      'icon-xs': 'size-6 rounded-[calc(var(--radius)-5px)] p-0 has-[>svg]:p-0',
      'icon-sm': 'size-8 p-0 has-[>svg]:p-0',
    },
  },
  defaultVariants: {
    size: 'xs',
  },
});

function InputGroupButton({
  className,
  type = 'button',
  variant = 'ghost',
  size = 'xs',
  ...props
}: Omit<React.ComponentProps<typeof Button>, 'size'> &
  VariantProps<typeof inputGroupButtonVariants>) {
  return (
    <Button
      type={type}
      data-size={size}
      variant={variant}
      className={cn(inputGroupButtonVariants({ size }), className)}
      {...props}
    />
  );
}

const inputGroupTextVariants = cva(
  "flex items-center gap-2 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      size: {
        md: 'text-sm',
        lg: 'text-base',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
);

function InputGroupText({
  className,
  size,
  ...props
}: React.ComponentProps<'span'> & VariantProps<typeof inputGroupTextVariants>) {
  const contextSize = useContext(InputGroupSizeContext);
  const computedSize = size ?? contextSize;

  return (
    <span
      data-size={computedSize}
      className={cn(inputGroupTextVariants({ size: computedSize }), className)}
      {...props}
    />
  );
}

const InputGroupInput = React.forwardRef<HTMLInputElement, React.ComponentProps<typeof Input>>(
  ({ className, size, ...props }, ref) => {
    const contextSize = useContext(InputGroupSizeContext);
    const computedSize = size ?? contextSize;

    return (
      <Input
        ref={ref}
        data-slot="input-group-control"
        className={cn(
          'flex-1 rounded-none border-0 bg-transparent shadow-none focus-visible:ring-0',
          className,
        )}
        size={computedSize}
        {...props}
      />
    );
  },
);

InputGroupInput.displayName = 'InputGroupInput';

const InputGroupTextarea = React.forwardRef<
  HTMLTextAreaElement,
  React.ComponentProps<typeof Textarea>
>(({ className, size, ...props }, ref) => {
  const contextSize = useContext(InputGroupSizeContext);
  const computedSize = size ?? contextSize;

  return (
    <Textarea
      ref={ref}
      data-slot="input-group-control"
      className={cn(
        'flex-1 resize-none rounded-none border-0 bg-transparent py-3 shadow-none focus-visible:ring-0',
        className,
      )}
      size={computedSize}
      {...props}
    />
  );
});

InputGroupTextarea.displayName = 'InputGroupTextarea';

export {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
  InputGroupText,
  InputGroupTextarea,
};
